import React, { FC } from 'react';

import AutosizeInput from 'react-input-autosize';
import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import { Label } from '../../label';
import { IAmountNumberInput, INumberAutosizeInput } from './types';

const NumberAutosizeInput = React.forwardRef<
  HTMLInputElement,
  INumberAutosizeInput
>(
  (
    {
      className,
      isInvalid,
      inputClassName,
      placeholder,
      value,
      onChange,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <AutosizeInput
        inputRef={(instance) => {
          if (typeof ref === 'function') {
            ref(instance);
          } else if (ref && 'current' in ref) {
            ref.current = instance;
          }
        }}
        {...props}
        value={value}
        onChange={onChange}
        disabled={disabled}
        inputClassName={twMerge(
          'bg-[transparent] outline-none appearance-none outline-none border-none shadow-none placeholder:text-base-100',
          disabled
            ? 'opacity-50'
            : isInvalid && 'text-negative-500 placeholder:text-negative-500',
          inputClassName,
        )}
        placeholder={
          placeholder && !isNaN(Number(placeholder)) ? placeholder : '0'
        }
        className={twMerge('w-full overflow-hidden', className)}
      />
    );
  },
);

export const AmountNumberInput: FC<IAmountNumberInput> = ({
  value,
  onValueChange,
  className,
  prefix,
  suffix,
  numberInputProps = {},
  numberAutosizeInputProps = {},
  isInvalid,
  wrapperClassName,
  size = 'default',
  ...props
}) => {
  return (
    <div
      className={twMerge(
        'flex w-full items-center justify-center overflow-hidden',
        wrapperClassName,
      )}
    >
      <Label
        className={twMerge(
          'flex w-max items-center justify-center gap-1 overflow-hidden uppercase',
          size === 'default' && 'heading-s',
          size === 'large' && 'heading-l',
          className,
        )}
      >
        {prefix}
        <NumericFormat
          value={value}
          onValueChange={({ value }) => {
            onValueChange?.(value);
          }}
          isInvalid={isInvalid}
          {...numberAutosizeInputProps}
          customInput={NumberAutosizeInput}
          allowNegative={numberInputProps?.allowNegative ?? false}
          decimalScale={numberInputProps?.decimalScale ?? 2}
          thousandSeparator={numberInputProps?.thousandSeparator ?? ' '}
          decimalSeparator={numberInputProps?.decimalSeparator ?? '.'}
          allowedDecimalSeparators={
            numberInputProps?.allowedDecimalSeparators ?? ['.', ',']
          }
          {...numberInputProps}
          {...props}
        />
        {suffix}
      </Label>
    </div>
  );
};
